// Symbolic text boxes (TT-1269, Eileen's sketches): a text box can SAY
// what its text is — a looped thought cloud for ideas, a radiant ring for a
// vision, an arrow for a direction, a scroll for history, an open book for a
// reference, a burst for an insight. Each is path data in the box's own
// coordinates, like terrain.js, so a motif resizes with the box (board chunks
// grow, scene ladders pick widths) and rides the same ink pass and hand
// wobble as the plain box it replaces. The names are the meaning, not the
// shape: a planner names a mood, the renderer draws it.
//
// Pure path data, no DOM. `rand` is any seeded () => [0, 1). A kind that is
// not a motif returns null / [] / zero reach everywhere, so callers fall
// through to terrain or their plain box.
//
// A motif paints OUTSIDE its rect — rays, curls, an arrowhead — by
// `motifReach`, which layout uses to keep neighbours clear, the way the
// scene's cloudReach does.

import { f, jitter, pt, scallop, terrainOutline, walk } from './terrain.js';

// fill = the wash inside; trim = the colour of the strokes outside the
// outline (rays, the scroll's roll, the book's gutter). Rays in warm gold
// read as light; the rest stay ink.
export const MOTIF_TINT = {
  idea: { fill: '#f5f2fb', trim: '#1f1d1a' },
  vision: { fill: '#fef8e4', trim: '#c98a1e' },
  direction: { fill: '#ffffff', trim: '#1f1d1a' },
  history: { fill: '#f9f3e3', trim: '#8a6a44' },
  reference: { fill: '#ffffff', trim: '#1f1d1a' },
  // the burst's rays are two-tone: a wide pale glow under a thinner amber line
  insight: { fill: '#fffbe6', trim: '#d9a21b', glow: '#f3d98b' },
};

export const MOTIFS = Object.keys(MOTIF_TINT);
export const isMotif = (kind) => Boolean(MOTIF_TINT[kind]);
// no paint outside the rect: a plain box, a terrain, a kind that is not a motif
export const NO_REACH = Object.freeze({ top: 0, right: 0, bottom: 0, left: 0 });

/**
 * How a text box's outline is inked, for any dress (a plain box and a
 * terrain take the default): `shade` on or off, the stroke's width (0 for
 * none), and `ink` when it is not the palette's ink. The burst has no ring —
 * its rays and wash are the shape; the sun's ring is a faint gold line (an
 * ink circle fought the glow); the arrow is a touch heavier so its head
 * holds up.
 */
export function motifStyle(kind) {
  switch (kind) {
    case 'insight':
      return { shade: false, strokeWidth: 0 };
    case 'vision':
      return { shade: false, strokeWidth: 1.6, ink: '#e0b45a' };
    case 'direction':
      return { shade: true, strokeWidth: 2.6 };
    default:
      return { shade: true, strokeWidth: 2 };
  }
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// ---- the shapes' measures (shared by the path, the trim and the reach) ----

// A radiant ring: an ellipse a little outside the rect (the text's own
// padding keeps the corners clear of the curve), rays from `gap` past it,
// each `k` × the rect's short side within [lo, hi].
const ringRadii = (r, kx, ky) => ({ cx: r.x + r.w / 2, cy: r.y + r.h / 2, rx: (r.w / 2) * kx, ry: (r.h / 2) * ky });
const VISION = { ring: (r) => ringRadii(r, 1.12, 1.2), gap: 8, k: 0.28, lo: 14, hi: 38 };
const INSIGHT = { ring: (r) => ringRadii(r, 1.05, 1.12), gap: 12, k: 0.45, lo: 18, hi: 54 };
const rayLen = (r, ray) => clamp(Math.min(r.w, r.h) * ray.k, ray.lo, ray.hi);
// the ring's overhang plus the rays, on each side
function ringReach(r, ray) {
  const { rx, ry } = ray.ring(r);
  const out = ray.gap + rayLen(r, ray) + 2;
  return { top: ry - r.h / 2 + out, bottom: ry - r.h / 2 + out, left: rx - r.w / 2 + out, right: rx - r.w / 2 + out };
}
const arrowHead = (r) => clamp(r.h * 0.7, 22, 70);
const arrowFlare = (r) => clamp(r.h * 0.5, 14, 40);
const scrollRoll = (r) => clamp(r.h * 0.12, 8, 16);
const scrollBow = (r) => clamp(r.w * 0.05, 5, 12);
const bookRise = (r) => clamp(r.h * 0.12, 8, 18);

/**
 * Units of paint outside the rect on each side, so layout can inflate a
 * motif's rect before placing neighbours. Zero all round for a non-motif.
 */
export function motifReach(kind, r) {
  switch (kind) {
    case 'idea': {
      const b = scallop(r) * 0.75; // a scallop peaks at half its control, which sits up to 1.2 × bump out
      return { top: b, right: b, bottom: b, left: b };
    }
    case 'vision':
      return ringReach(r, VISION);
    case 'direction':
      return { top: arrowFlare(r), bottom: arrowFlare(r), left: 0, right: arrowHead(r) };
    case 'history': {
      const roll = scrollRoll(r);
      return { top: roll * 1.4, bottom: roll * 1.6, left: roll * 1.3, right: roll * 1.3 };
    }
    case 'reference':
      return { top: bookRise(r), bottom: 8, left: 4, right: 4 };
    case 'insight':
      return ringReach(r, INSIGHT);
    default:
      return NO_REACH;
  }
}

// ---- outlines --------------------------------------------------------------

/**
 * Idea: the plain thought cloud — the terrain's sky ring in its own tint.
 * Eileen's sketch had loops on the bumps; drawn, they read as beads on the
 * outline, so the cloud keeps the ring and the mood lives in the tint.
 */
const ideaPath = (r, rand) => terrainOutline('sky', r, rand);

/** A closed ring on an ellipse, breathing a little (one slow bulge) and jittered. */
function ringPath({ cx, cy, rx, ry }, rand, wobble) {
  const n = Math.max(24, Math.round((rx + ry) / 6));
  const phase = rand() * Math.PI * 2;
  let d = '';
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const k = 1 + 0.035 * Math.sin(t * 2 + phase);
    d += `${i ? 'L' : 'M'}${pt(cx + Math.cos(t) * rx * k + jitter(rand, wobble), cy + Math.sin(t) * ry * k + jitter(rand, wobble))}`;
  }
  return d + 'Z';
}

/**
 * Direction: an arrow pointing right. The text rides the shaft; the head
 * flares past the rect's top and bottom.
 */
function arrowPath(r, rand, wobble) {
  const { x, y, w, h } = r;
  const head = arrowHead(r);
  const flare = arrowFlare(r);
  const x1 = x + w;
  const j = () => jitter(rand, wobble);
  let d = walk([[x, y, x1, y, 10]], rand, wobble);
  d += ` L${pt(x1 + j(), y - flare + j())} L${pt(x1 + head + j(), y + h / 2 + j())} L${pt(x1 + j(), y + h + flare + j())}`;
  d += walk([[x1, y + h, x, y + h, 10]], rand, wobble, ' L');
  return d + 'Z';
}

/**
 * History: a scroll. Both long edges bow the same way, like paper that has
 * been rolled; the roll itself (the tube at the top, the loose lines at the
 * foot, the curled ends) is trim.
 */
function scrollPath(r, rand, wobble) {
  const { x, y, w, h } = r;
  const bow = scrollBow(r);
  const j = () => jitter(rand, wobble);
  let d = walk([[x, y, x + w, y, 8]], rand, wobble);
  d += ` C${pt(x + w + bow * 2.4, y + h * 0.3)} ${pt(x + w - bow * 2.4, y + h * 0.7)} ${pt(x + w + j(), y + h + j())}`;
  d += walk([[x + w, y + h, x, y + h, 8]], rand, wobble, ' L');
  d += ` C${pt(x - bow * 2.4, y + h * 0.7)} ${pt(x + bow * 2.4, y + h * 0.3)} ${pt(x + j(), y + j())}`;
  return d + 'Z';
}

/**
 * Reference: an open book. Each page's top arcs up and they meet in a dip at
 * the gutter; the gutter crease, the turned page-feet and the base are trim.
 */
function bookPath(r, rand, wobble) {
  const { x, y, w, h } = r;
  const rise = bookRise(r);
  const cx = x + w / 2;
  const j = () => jitter(rand, wobble);
  let d = `M${pt(x + j(), y + h + j())}`;
  d += walk([[x, y + h, x, y, 5]], rand, wobble, ' L');
  d += ` Q${pt(x + w * 0.25, y - rise)} ${pt(cx + j(), y + rise * 0.6)}`;
  d += ` Q${pt(cx + w * 0.25, y - rise)} ${pt(x + w + j(), y + j())}`;
  d += walk([[x + w, y, x + w, y + h, 5]], rand, wobble, ' L');
  d += ` Q${pt(cx, y + h + rise * 0.35)} ${pt(x + j(), y + h + j())}`;
  return d + 'Z';
}

/**
 * The text box's silhouette for a motif, closed path data in the rect's
 * coordinates, or null for a kind that is not a motif. `wobble` is the
 * hand's jitter in units.
 */
export function motifOutline(kind, r, rand, wobble = 2.2) {
  switch (kind) {
    case 'idea':
      return ideaPath(r, rand);
    case 'vision':
      return ringPath(VISION.ring(r), rand, wobble * 0.6);
    case 'direction':
      return arrowPath(r, rand, wobble);
    case 'history':
      return scrollPath(r, rand, wobble);
    case 'reference':
      return bookPath(r, rand, wobble);
    case 'insight':
      // a paper patch under the text with no ink of its own (motifStyle)
      return ringPath(INSIGHT.ring(r), rand, 0.5);
    default:
      return null;
  }
}

// ---- trim --------------------------------------------------------------------

/**
 * Rays around an ellipse: `n` dashes from `gap` past the ring, each its own
 * length (short ones among long), angles evenly spread then nudged.
 */
function rays({ cx, cy, rx, ry }, n, gap, len, rand, spread = 0.45) {
  const out = [];
  const phase = rand() * Math.PI * 2;
  for (let i = 0; i < n; i++) {
    const t = phase + ((i + (rand() - 0.5) * spread) / n) * Math.PI * 2;
    const ux = Math.cos(t);
    const uy = Math.sin(t);
    // from the ring outward along the radial, the ring's radius at this angle
    const rr = 1 / Math.sqrt((ux * ux) / (rx * rx) + (uy * uy) / (ry * ry));
    const g = gap * (0.8 + rand() * 0.6);
    const l = len * (rand() < 0.3 ? 0.45 + rand() * 0.25 : 0.75 + rand() * 0.35);
    out.push(`M${pt(cx + ux * (rr + g), cy + uy * (rr + g))} L${pt(cx + ux * (rr + g + l), cy + uy * (rr + g + l))}`);
  }
  return out;
}

/** A curled end of the scroll: a small hook, `dx`/`dy` give its direction. */
const curl = (px, py, s, dx, dy) => `M${pt(px, py)} q${f(dx * s)},${f(dy * s * 0.2)} ${f(dx * s * 0.9)},${f(dy * s)} q${f(-dx * s * 0.1)},${f(dy * s * 0.7)} ${f(-dx * s * 0.7)},${f(dy * s * 0.55)}`;

/**
 * Trim: the strokes that make the motif, drawn AFTER the outline (they may
 * cross it). Each item: { d, stroke, fill, strokeWidth, opacity, kind:
 * 'stroke' | 'wash' } — the terrain trim's shape, so one mount serves both.
 */
export function motifTrim(kind, r, rand) {
  const { x, y, w, h } = r;
  const tint = MOTIF_TINT[kind];
  if (!tint) return [];
  const out = [];
  const stroke = (d, sw = 1.6, opacity = 0.85, colour = tint.trim) =>
    out.push({ d, stroke: colour, fill: 'none', strokeWidth: sw, opacity, kind: 'stroke' });
  switch (kind) {
    case 'vision': {
      const n = 9 + Math.round(rand() * 3);
      for (const d of rays(VISION.ring(r), n, VISION.gap, rayLen(r, VISION), rand)) stroke(d, 2.4, 0.9);
      break;
    }
    case 'insight': {
      // two passes per ray: the glow (wide, pale) under the line (thinner, amber)
      const n = 14 + Math.round(rand() * 4);
      const ds = rays(INSIGHT.ring(r), n, INSIGHT.gap, rayLen(r, INSIGHT), rand, 0.3);
      for (const d of ds) stroke(d, 6, 0.85, tint.glow);
      for (const d of ds) stroke(d, 2.2, 0.95);
      break;
    }
    case 'history': {
      const roll = scrollRoll(r);
      // the tube at the top: a second line under the top edge, and a curl at each end
      stroke(walk([[x + roll * 1.6, y + roll * 0.9, x + w - roll * 1.4, y + roll * 0.9, 8]], rand, 1.2), 1.5, 0.7);
      stroke(curl(x, y, roll * 1.3, -1, -1), 1.8, 0.9);
      stroke(curl(x + w, y, roll * 1.3, 1, -1), 1.8, 0.9);
      // the loose end at the foot: two lines running out under the bottom edge
      stroke(walk([[x + roll * 2, y + h + roll * 0.55, x + w - roll * 0.5, y + h + roll * 0.45, 8]], rand, 1.2), 1.5, 0.7);
      stroke(walk([[x + roll * 2.6, y + h + roll * 1.15, x + w - roll * 1.2, y + h + roll * 1.05, 8]], rand, 1.2), 1.4, 0.55);
      stroke(curl(x + roll * 2.2, y + h + roll * 0.55, roll * 1.2, -1, 1), 1.6, 0.8);
      stroke(curl(x + w - roll * 0.4, y + h + roll * 0.45, roll * 1.2, 1, 1), 1.6, 0.8);
      break;
    }
    case 'reference': {
      const rise = bookRise(r);
      const cx = x + w / 2;
      // the gutter crease, light, under the text
      stroke(walk([[cx, y + rise * 0.6, cx, y + h, 6, 1.2]], rand, 1.2), 1.4, 0.3);
      // the page-feet: a turned corner on each page
      const foot = clamp(h * 0.08, 5, 11);
      stroke(`M${pt(x + w * 0.1, y + h)} q${f(w * 0.16)},${f(-foot * 2)} ${f(w * 0.32)},0`, 1.5, 0.6);
      stroke(`M${pt(x + w * 0.58, y + h)} q${f(w * 0.16)},${f(-foot * 2)} ${f(w * 0.32)},0`, 1.5, 0.6);
      // the base: the book's bottom board, a touch wider than the pages
      stroke(walk([[x - 4, y + h + 6, x + w + 4, y + h + 6, 8, 1]], rand, 1), 2.2, 0.9);
      break;
    }
    default:
      break;
  }
  return out;
}
